import { HeaderCellProps, TableRowData } from 'tableTypes'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { formatNumberToNumberWithSpecifiedDecimals } from 'utils/commonUtils'
import { MappedTarget } from 'reportViewer'
import { mappedTargetToTableRowData } from '../../mappers/targetMappers'
import {
    getMaxGenomateScoreFromLeveledList,
    getMinGenomateScoreFromLeveledList
} from '../../utils/mtcUtils'
import LinkButton from '../button/LinkButton'
import DefaultCard from '../card/DefaultCard'
import ProgressBarWithValueIndicator from '../progressbar/ProgressBarWithValueIndicator'
import TableWithGenomateScoreColumn from '../table/TableWithGenomateScoreColumn'

export type Props = {
    targets: Array<MappedTarget>
    hideSubtitle?: boolean
    hideSeeAllButton?: boolean
    refKey?: string
}

const DruggableTargetsCard = ({
    targets,
    hideSeeAllButton,
    refKey,
    hideSubtitle
}: Props) => {
    const [minItemsForButton] = React.useState(0)
    const [tableRows, setTableRows] = React.useState<Array<TableRowData>>([])
    const [maxGts, setMaxGts] = React.useState<number>(0)
    const [minGts, setMinGts] = React.useState<number>(0)

    const { t } = useTranslation('targets')
    const location = useLocation()
    const navigate = useNavigate()

    const headerCells: Array<HeaderCellProps> = [
        { id: 'target', sortable: true, type: 'string' },
        { id: 'relatedDrivers', sortable: false },
        { id: 'gts', sortable: true, type: 'number' }
    ]

    React.useEffect(() => {
        setTableRows(
            targets.map((mappedTarget, idx) =>
                mappedTargetToTableRowData(
                    mappedTarget,
                    location.pathname,
                    idx,
                    t
                )
            )
        )

        setMaxGts(
            getMaxGenomateScoreFromLeveledList(
                targets.map((target) => ({
                    level: formatNumberToNumberWithSpecifiedDecimals(
                        target.level,
                        2
                    )
                }))
            )
        )

        setMinGts(
            getMinGenomateScoreFromLeveledList(
                targets.map((target) => ({
                    level: formatNumberToNumberWithSpecifiedDecimals(
                        target.level,
                        2
                    )
                }))
            )
        )
    }, [location.pathname, t, targets])

    const openTargetsPage = () => {
        if (location) {
            const redirectUrl = `${location.pathname}/targets`

            navigate(redirectUrl)
        }
    }

    return (
        <DefaultCard
            refKey={refKey}
        >
            {tableRows.length > 0 ? (
                <div className="druggableTargets__tableWrapper defaultCard__tableWrapper">
                    <TableWithGenomateScoreColumn<'target' | 'gts'>
                        header={{
                            cells: headerCells,
                            translationNamespace: 'targets',
                            translationKey: 'tableHeader'
                        }}
                        tableRows={tableRows}
                    >
                        <ProgressBarWithValueIndicator
                            max={maxGts}
                            min={minGts}
                        />
                    </TableWithGenomateScoreColumn>

                    {!hideSeeAllButton &&
                        tableRows.length > minItemsForButton && (
                            <LinkButton
                                label={t('seeAll')}
                                onClick={openTargetsPage}
                            />
                        )}
                </div>
            ) : (
                <span className="druggableTargets__emptyState defaultCard__emptyState">
                    {t('noDruggableTargetsFound')}
                </span>
            )}
        </DefaultCard>
    )
}

export default DruggableTargetsCard
